import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEnhancedLeadSearch } from '@/hooks/useEnhancedLeadSearch';
import { useContactSearch } from '@/hooks/useContactSearch';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Building2, User } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Event } from '@/types/scheduler';
import { fromApiFormat, formatInLocalTime, nowInLocal } from '@/utils/timezone';
import { fetchLeadById } from '@/services/api/leads';
import { fetchContactById } from '@/services/leadContactsService';

// Helper function to round time to nearest 15 minutes
const roundToNearestQuarter = (date: Date): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;
  
  if (remainder === 0) {
    return rounded;
  }
  
  // Round to next 15-minute mark
  const minutesToAdd = 15 - remainder;
  rounded.setMinutes(minutes + minutesToAdd);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);
  
  return rounded;
};

// Helper function to get current time defaults
const getCurrentTimeDefaults = (selectedDate?: Date | null) => {
  const now = nowInLocal();
  const baseDate = selectedDate || now;
  
  // If selectedDate is provided and is different day, use 9 AM as default
  if (selectedDate && selectedDate.toDateString() !== now.toDateString()) {
    const startDate = new Date(selectedDate);
    startDate.setHours(9, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(15);
    return { startDate, endDate };
  }
  
  // For current day or no selected date, use current time rounded to nearest 15 min
  const startDate = roundToNearestQuarter(now);
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + 15);
  
  return { startDate, endDate };
};

const eventSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  relatedLeadId: z.string().optional(),
  relatedContactId: z.string().optional(),
  startDate: z.date(),
  endDate: z.date(),
  eventType: z.enum(['meeting', 'email', 'call', 'task', 'reminder', 'follow_up', 'campaign', 'other']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  location: z.string().optional(),
}).refine(data => data.endDate >= data.startDate, {
  message: "End date must be after start date",
  path: ["endDate"],
});

type EventFormData = z.infer<typeof eventSchema>;

interface EventSubmitData extends Omit<EventFormData, 'relatedLeadId' | 'relatedContactId'> {
  relatedLeadId?: number;
  relatedContactId?: number;
}

interface EventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: Event | null;
  selectedDate?: Date | null;
  onSave: (event: EventSubmitData) => Promise<void>;
  relatedLeadId?: number;
  leadSearchTerm?: string;
}

export const EventDialog: React.FC<EventDialogProps> = ({
  open,
  onOpenChange,
  event,
  selectedDate,
  onSave,
  relatedLeadId,
  leadSearchTerm: initialLeadSearchTerm,
}) => {
  const { leads, loading: leadLoading, searchLeads } = useEnhancedLeadSearch();
  const { contacts, loading: contactLoading, searchContacts, loadContactsForLead, clearContacts } = useContactSearch();
  
  const [leadSearchTerm, setLeadSearchTerm] = React.useState(initialLeadSearchTerm || '');
  const [contactSearchTerm, setContactSearchTerm] = React.useState('');
  const [showLeadResults, setShowLeadResults] = React.useState(false);
  const [showContactResults, setShowContactResults] = React.useState(false);
  const [isAutoGeneratedTitle, setIsAutoGeneratedTitle] = React.useState(false);
  const [isAutoGeneratedDescription, setIsAutoGeneratedDescription] = React.useState(false);
  
  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: event?.title || '',
      description: event?.description || '',
      relatedLeadId: event?.related_lead_id?.toString() || relatedLeadId?.toString() || '',
      relatedContactId: event?.related_contact_id?.toString() || '',
      startDate: event ? fromApiFormat(event.start_datetime) : nowInLocal(),
      endDate: event && event.end_datetime ? fromApiFormat(event.end_datetime) : nowInLocal(),
      eventType: event?.event_type || undefined,
      priority: event?.priority || 'medium',
      location: event?.location || '',
    },
  });

  // Handle lead selection
  const handleLeadSelect = (lead: any) => {
    form.setValue('relatedLeadId', lead.id.toString());
    setLeadSearchTerm(lead.name);
    setShowLeadResults(false);
    
    // Clear contact if it doesn't belong to selected lead
    const selectedContact = form.getValues('relatedContactId');
    if (selectedContact) {
      const contact = contacts.find(c => c.id.toString() === selectedContact);
      if (contact && contact.lead_id?.toString() !== lead.id.toString()) {
        form.setValue('relatedContactId', '');
        setContactSearchTerm('');
      }
    }

    // Auto-load contacts for the selected lead
    loadContactsForLead(lead.id);
  };

  const handleContactSelect = (contact: any) => {
    form.setValue('relatedContactId', contact.id.toString());
    setContactSearchTerm(`${contact.first_name} ${contact.last_name}`);
    setShowContactResults(false);
    
    // Auto-set lead if contact has one
    if (contact.lead_id) {
      form.setValue('relatedLeadId', contact.lead_id.toString());
      const lead = leads.find(l => l.id.toString() === contact.lead_id.toString());
      if (lead) {
        setLeadSearchTerm(lead.name);
      }
    }
  };

  const handleLeadSearch = (term: string) => {
    setLeadSearchTerm(term);
    if (term.length >= 2) {
      searchLeads(term);
      setShowLeadResults(true);
    } else {
      setShowLeadResults(false);
    }
  };

  const handleContactSearch = (term: string) => {
    setContactSearchTerm(term);
    if (term.length >= 2) {
      const selectedLeadId = form.getValues('relatedLeadId');
      searchContacts(term, selectedLeadId ? parseInt(selectedLeadId) : undefined);
      setShowContactResults(true);
    } else {
      setShowContactResults(false);
    }
  };

  // Auto-generation functions
  const generateTitle = React.useCallback(() => {
    const values = form.getValues();
    const { eventType, startDate, relatedLeadId, relatedContactId } = values;
    
    if (!eventType || !startDate) return '';
    
    const lead = leads.find(l => l.id.toString() === relatedLeadId);
    const contact = contacts.find(c => c.id.toString() === relatedContactId);
    
    // Use leadSearchTerm as fallback if lead object not found in search results
    const leadName = lead?.name || (relatedLeadId && leadSearchTerm ? leadSearchTerm : null);
    
    const date = formatInLocalTime(startDate, 'MMMM d, yyyy');
    const type = eventType.charAt(0).toUpperCase() + eventType.slice(1).replace('_', ' ');
    
    if (contact && leadName) {
      return `${leadName}: ${type} scheduled on ${date} with ${contact.first_name} ${contact.last_name}`;
    } else if (contact) {
      return `${type} scheduled on ${date} with ${contact.first_name} ${contact.last_name}`;
    } else if (leadName) {
      return `${leadName}: ${type} scheduled on ${date}`;
    } else {
      return `${type} scheduled on ${date}`;
    }
  }, [form, leads, contacts, leadSearchTerm]);

  const generateDescription = React.useCallback(() => {
    const values = form.getValues();
    const { eventType, startDate, endDate, priority, relatedLeadId, relatedContactId } = values;
    
    if (!eventType || !startDate || !endDate || !priority) return '';
    
    const lead = leads.find(l => l.id.toString() === relatedLeadId);
    const contact = contacts.find(c => c.id.toString() === relatedContactId);
    
    // Use leadSearchTerm as fallback if lead object not found in search results
    const leadName = lead?.name || (relatedLeadId && leadSearchTerm ? leadSearchTerm : null);
    
    const date = formatInLocalTime(startDate, 'MMMM d, yyyy');
    const startTime = formatInLocalTime(startDate, 'h:mm a');
    const endTime = formatInLocalTime(endDate, 'h:mm a');
    const type = eventType.charAt(0).toUpperCase() + eventType.slice(1).replace('_', ' ').toLowerCase();
    const priorityCapitalized = priority.charAt(0).toUpperCase() + priority.slice(1);
    
    if (contact && leadName) {
      return `${priorityCapitalized} priority ${type} scheduled for ${date} from ${startTime} to ${endTime} with ${contact.first_name} ${contact.last_name} (${leadName})`;
    } else if (contact) {
      return `${priorityCapitalized} priority ${type} scheduled for ${date} from ${startTime} to ${endTime} with ${contact.first_name} ${contact.last_name}`;
    } else if (leadName) {
      return `${priorityCapitalized} priority ${type} scheduled for ${date} from ${startTime} to ${endTime} for ${leadName}`;
    } else {
      return `${priorityCapitalized} priority ${type} scheduled for ${date} from ${startTime} to ${endTime}`;
    }
  }, [form, leads, contacts, leadSearchTerm]);

  // Auto-generate title and description when form values change
  React.useEffect(() => {
    const currentTitle = form.getValues('title');
    if ((!currentTitle || isAutoGeneratedTitle)) {
      const generatedTitle = generateTitle();
      if (generatedTitle && generatedTitle !== currentTitle) {
        form.setValue('title', generatedTitle);
        setIsAutoGeneratedTitle(true);
      }
    }
  }, [form.watch('eventType'), form.watch('startDate'), form.watch('relatedLeadId'), form.watch('relatedContactId'), generateTitle, isAutoGeneratedTitle, event]);

  React.useEffect(() => {
    const currentDescription = form.getValues('description');
    if ((!currentDescription || isAutoGeneratedDescription)) {
      const generatedDescription = generateDescription();
      if (generatedDescription && generatedDescription !== currentDescription) {
        form.setValue('description', generatedDescription);
        setIsAutoGeneratedDescription(true);
      }
    }
  }, [form.watch('eventType'), form.watch('startDate'), form.watch('endDate'), form.watch('priority'), form.watch('relatedLeadId'), form.watch('relatedContactId'), generateDescription, isAutoGeneratedDescription, event]);

  React.useEffect(() => {
    if (event) {
      const startLocal = fromApiFormat(event.start_datetime);
      const endLocal = event.end_datetime ? fromApiFormat(event.end_datetime) : nowInLocal();
      
      form.reset({
        title: event.title,
        description: event.description || '',
        relatedLeadId: event.related_lead_id?.toString() || '',
        relatedContactId: event.related_contact_id?.toString() || '',
        startDate: startLocal,
        endDate: endLocal,
        eventType: event.event_type,
        priority: event.priority,
        location: event.location || '',
      });
      
      // Fetch and display lead and contact names for edit mode
      const fetchLeadAndContactNames = async () => {
        try {
          console.log('🔍 EventDialog: Starting to fetch lead/contact names for event:', event);
          
          // Fetch lead name if event has related_lead_id
          if (event.related_lead_id) {
            console.log(`🔍 EventDialog: Fetching lead with ID: ${event.related_lead_id}`);
            const leadData = await fetchLeadById(event.related_lead_id.toString());
            console.log('🔍 EventDialog: Lead data response:', leadData);
            
            // Handle the API response structure: {success: true, lead: {...}}
            if (leadData && leadData.success && leadData.lead && leadData.lead.name) {
              console.log(`✅ EventDialog: Setting lead search term to: ${leadData.lead.name}`);
              setLeadSearchTerm(leadData.lead.name);
            } else if (leadData && leadData.name) {
              // Fallback for direct lead object
              console.log(`✅ EventDialog: Setting lead search term (fallback) to: ${leadData.name}`);
              setLeadSearchTerm(leadData.name);
            } else {
              console.warn('⚠️ EventDialog: Lead data structure not recognized:', leadData);
            }
          }
          
          // Fetch contact name if event has related_contact_id
          if (event.related_contact_id) {
            console.log(`🔍 EventDialog: Fetching contact with ID: ${event.related_contact_id}`);
            const contactData = await fetchContactById(event.related_contact_id.toString());
            console.log('🔍 EventDialog: Contact data response:', contactData);
            
            // Handle the API response structure: {contact: {...}, lead: {...}}
            if (contactData && contactData.contact) {
              const fullName = `${contactData.contact.first_name} ${contactData.contact.last_name}`.trim();
              console.log(`✅ EventDialog: Setting contact search term to: ${fullName}`);
              setContactSearchTerm(fullName);
            } else {
              console.warn('⚠️ EventDialog: Contact data structure not recognized:', contactData);
            }
          }
        } catch (error) {
          console.error('💥 EventDialog: Error fetching lead/contact names for edit mode:', error);
          // Reset to empty if fetch fails
          setLeadSearchTerm('');
          setContactSearchTerm('');
        }
      };
      
      fetchLeadAndContactNames();
      setIsAutoGeneratedTitle(false);
      setIsAutoGeneratedDescription(false);
    } else {
      const { startDate, endDate } = getCurrentTimeDefaults(selectedDate);
      
      form.reset({
        title: '',
        description: '',
        relatedLeadId: relatedLeadId?.toString() || '',
        relatedContactId: '',
        startDate,
        endDate,
        eventType: undefined,
        priority: 'medium',
        location: '',
      });
      
      setLeadSearchTerm(initialLeadSearchTerm || '');
      setContactSearchTerm('');
      setIsAutoGeneratedTitle(false);
      setIsAutoGeneratedDescription(false);
      
      // Fetch lead name if relatedLeadId is provided but no leadSearchTerm
      if (relatedLeadId && !initialLeadSearchTerm) {
        const fetchLeadName = async () => {
          try {
            console.log(`🔍 EventDialog: Fetching lead name for ID: ${relatedLeadId}`);
            const leadData = await fetchLeadById(relatedLeadId.toString());
            console.log('🔍 EventDialog: Lead data response:', leadData);
            
            if (leadData && leadData.success && leadData.lead && leadData.lead.name) {
              console.log(`✅ EventDialog: Setting lead search term to: ${leadData.lead.name}`);
              setLeadSearchTerm(leadData.lead.name);
            } else if (leadData && leadData.name) {
              console.log(`✅ EventDialog: Setting lead search term (fallback) to: ${leadData.name}`);
              setLeadSearchTerm(leadData.name);
            }
          } catch (error) {
            console.error('💥 EventDialog: Error fetching lead name:', error);
          }
        };
        
        fetchLeadName();
        
        // Auto-load contacts for the related lead
        loadContactsForLead(relatedLeadId);
      }
    }
    setShowLeadResults(false);
    setShowContactResults(false);
  }, [event, selectedDate, form, relatedLeadId, initialLeadSearchTerm, loadContactsForLead]);

  // Auto-update end time when start time changes (for new events only)
  React.useEffect(() => {
    // Only auto-update for new events, not when editing
    if (event) return;
    
    const startDate = form.watch('startDate');
    if (startDate) {
      const currentEndDate = form.getValues('endDate');
      
      // Calculate duration between current start and end
      const duration = currentEndDate && startDate ? 
        currentEndDate.getTime() - startDate.getTime() : 
        15 * 60 * 1000; // Default 15 minutes
      
      // Use minimum 15 minutes duration
      const actualDuration = Math.max(duration, 15 * 60 * 1000);
      
      const newEndDate = new Date(startDate.getTime() + actualDuration);
      form.setValue('endDate', newEndDate);
    }
  }, [form.watch('startDate'), event, form]);

  const onSubmit = async (data: EventFormData) => {
    const submitData: EventSubmitData = {
      title: data.title,
      description: data.description,
      startDate: data.startDate,
      endDate: data.endDate,
      eventType: data.eventType,
      priority: data.priority,
      location: data.location,
      relatedLeadId: data.relatedLeadId ? parseInt(data.relatedLeadId) : undefined,
      relatedContactId: data.relatedContactId ? parseInt(data.relatedContactId) : undefined,
    };
    
    await onSave(submitData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {event ? 'Edit Event' : 'Create Event'}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title*</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Event title" 
                      {...field} 
                      onChange={(e) => {
                        field.onChange(e);
                        setIsAutoGeneratedTitle(false);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Event description" 
                      {...field} 
                      onChange={(e) => {
                        field.onChange(e);
                        setIsAutoGeneratedDescription(false);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="relatedLeadId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Lead (Optional)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search for a lead..."
                            value={leadSearchTerm}
                            onChange={(e) => handleLeadSearch(e.target.value)}
                            onFocus={() => leadSearchTerm.length >= 2 && setShowLeadResults(true)}
                            onBlur={() => setTimeout(() => setShowLeadResults(false), 200)}
                            className="pl-10"
                          />
                        </div>
                        
                        {showLeadResults && leads.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {leads.map((lead) => (
                              <Card key={lead.id} className="cursor-pointer hover:bg-muted/50 transition-colors border-0 rounded-none">
                                <CardContent className="p-3" onClick={() => handleLeadSelect(lead)}>
                                  <div className="flex items-center space-x-3">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-sm">{lead.name}</p>
                                      {lead.industry && (
                                        <p className="text-xs text-muted-foreground">{lead.industry}</p>
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                        
                        {leadLoading && (
                          <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3">
                            <p className="text-sm text-muted-foreground">Searching leads...</p>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="relatedContactId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Related Contact (Optional)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                           <Input
                            placeholder={form.getValues('relatedLeadId') ? "Search contacts or view all below..." : "Search for a contact..."}
                            value={contactSearchTerm}
                            onChange={(e) => handleContactSearch(e.target.value)}
                            onFocus={() => {
                              if (contactSearchTerm.length >= 2) {
                                setShowContactResults(true);
                              } else if (form.getValues('relatedLeadId') && contacts.length > 0) {
                                setShowContactResults(true);
                              }
                            }}
                            onBlur={() => setTimeout(() => setShowContactResults(false), 200)}
                            className="pl-10"
                          />
                        </div>
                        
                        {((showContactResults && contacts.length > 0) || (form.getValues('relatedLeadId') && contacts.length > 0 && !contactSearchTerm)) && (
                          <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {form.getValues('relatedLeadId') && !contactSearchTerm && (
                              <div className="p-2 bg-muted/30 border-b">
                                <p className="text-xs text-muted-foreground font-medium">
                                  Contacts from {leadSearchTerm || 'selected lead'}:
                                </p>
                              </div>
                            )}
                            {contacts.map((contact) => {
                              // Parse email if it's JSON string
                              let displayEmail = contact.email;
                              if (contact.email && contact.email.startsWith('[')) {
                                try {
                                  const emailArray = JSON.parse(contact.email);
                                  if (Array.isArray(emailArray) && emailArray.length > 0) {
                                    const primaryEmail = emailArray.find(e => e.is_primary) || emailArray[0];
                                    displayEmail = primaryEmail.email;
                                  }
                                } catch (e) {
                                  console.warn('Failed to parse email JSON:', contact.email);
                                }
                              }
                              
                              return (
                                <Card key={contact.id} className="cursor-pointer hover:bg-muted/50 transition-colors border-0 rounded-none">
                                  <CardContent className="p-3" onClick={() => handleContactSelect(contact)}>
                                    <div className="flex items-center space-x-3">
                                      <User className="h-4 w-4 text-muted-foreground" />
                                      <div>
                                        <p className="font-medium text-sm">{contact.first_name} {contact.last_name}</p>
                                        {displayEmail && (
                                          <p className="text-xs text-muted-foreground">{displayEmail}</p>
                                        )}
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        )}
                        
                        {contactLoading && (
                          <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3">
                            <p className="text-sm text-muted-foreground">Searching contacts...</p>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date & Time*</FormLabel>
                    <p className="text-xs text-muted-foreground mb-1">Times are saved in GMT+6 (Dhaka)</p>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        value={field.value ? (() => {
                          try {
                            return formatInLocalTime(field.value, "yyyy-MM-dd'T'HH:mm");
                          } catch (error) {
                            console.error('Error formatting start date:', error);
                            return '';
                          }
                        })() : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            // datetime-local already provides correct local time
                            field.onChange(new Date(value));
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End Date & Time*</FormLabel>
                    <p className="text-xs text-muted-foreground mb-1">Times are saved in GMT+6 (Dhaka)</p>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        value={field.value ? (() => {
                          try {
                            return formatInLocalTime(field.value, "yyyy-MM-dd'T'HH:mm");
                          } catch (error) {
                            console.error('Error formatting end date:', error);
                            return '';
                          }
                        })() : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            // datetime-local already provides correct local time
                            field.onChange(new Date(value));
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="eventType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type*</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="task">Task</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                        <SelectItem value="follow_up">Follow Up</SelectItem>
                        <SelectItem value="campaign">Campaign</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Event location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {event ? 'Update' : 'Create'} Event
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};